import classNames from "classnames";
import type { RefObject } from "react";
import { forwardRef, useCallback, useImperativeHandle, useRef } from "react";
import type SimpleBarCore from "simplebar-core";
import SimpleBar, { Props as SimpleBarProps } from "simplebar-react";
import styles from "./index.module.css";

interface IProps extends SimpleBarProps {
  children: React.ReactNode;
  onScrollableNode?: (node: HTMLElement | null) => void;
}

export interface ScrollViewRef {
  recalculate: () => void;
}

export const ScrollView = forwardRef<ScrollViewRef, IProps>(function ScrollView(
  props,
  ref
) {
  const {
    className,
    onScrollableNode,
    scrollableNodeProps,
    children,
    ...rest
  } = props;

  const simpleBarRef = useRef<SimpleBarCore | null>(null);

  useImperativeHandle(
    ref,
    () => ({
      recalculate: () => {
        simpleBarRef.current?.recalculate();
      },
    }),
    []
  );

  const handleScrollableNodeRef = useCallback(
    (node: HTMLElement | null) => {
      if (typeof scrollableNodeProps?.ref === "function") {
        scrollableNodeProps.ref(node);
      } else if (scrollableNodeProps?.ref) {
        const refObject = scrollableNodeProps.ref as RefObject<HTMLElement>;
        if (refObject && "current" in refObject) {
          (refObject as RefObject<HTMLElement | null>).current = node;
        }
      }

      onScrollableNode?.(node);
    },
    [scrollableNodeProps?.ref, onScrollableNode]
  );

  return (
    <SimpleBar
      ref={simpleBarRef}
      {...rest}
      className={classNames(styles.wrapper, className)}
      scrollableNodeProps={{
        ...scrollableNodeProps,
        ref: handleScrollableNodeRef,
      }}
    >
      {children}
    </SimpleBar>
  );
});
